import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { ENV } from 'shared/ENV';

const DEFAULT_PERSIST_CACHE_TIME = 1000 * 60 * 60 * 24; // 24 hrs

type CacheableQueryClientProviderProps = {
  children: React.ReactNode;
};

// setting default to false in test but not setting a default otherwise
const defaultShouldRetry = ENV.VITE_ENV === 'test' ? false : undefined;

export const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
});

export const persistedQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: DEFAULT_PERSIST_CACHE_TIME,
      retry: defaultShouldRetry,
    },
    mutations: {
      retry: defaultShouldRetry,
    },
  },
});

persistQueryClient({
  queryClient: persistedQueryClient,
  persister: localStoragePersister,
  maxAge: 1000 * 60 * 60 * 24 * 30, // 30 days
});

export const unpersistedQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: defaultShouldRetry,
    },
    mutations: {
      retry: defaultShouldRetry,
    },
  },
});

export const CacheableQueryClientProvider = (
  props: CacheableQueryClientProviderProps,
): JSX.Element => {
  const { children } = props;

  return (
    <QueryClientProvider client={persistedQueryClient}>
      <QueryClientProvider client={unpersistedQueryClient}>
        {children}
      </QueryClientProvider>
    </QueryClientProvider>
  );
};
